import React, { useState, useEffect, useCallback } from "react"
import Link from "next/link"
import styled from "styled-components"
import { observer } from "mobx-react"
import dynamic from "next/dynamic"
import { useTranslations } from "next-intl"

import DesktopContainer from "~/components/DesktopContainer"
import Spacer from "~/components/Spacer"
import Hamburger from "./Hamburger"
import UserAvatar from "~/components/dashboard/Layout/Header/OldAppHeader/UserAvatar"
import SubmitTicketButton from "./SubmitTicketButton"
import WinItButton from "~/components/WinItButton"
import useInject from "~/hooks/useInject"
import payNYCTicketsLogo from "~/public/static/images/png/PayNYC.png"
import winitLogo from "~/public/static/images/svg/logo-shadow.svg"

const LoginModal = dynamic(() => import("~/components/auth/LoginModal"))
const SignupModal = dynamic(() => import("~/components/auth/SignUpModal"))

export const minHeightDesktop = "70px"
export const minHeightMobile = "50px"

const HeaderContainer = styled.div`
  border-bottom: solid 1px ${props => props.theme.colors.old.lightBorder};
  min-height: ${minHeightDesktop};
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${props => props.theme.media.mobile`
    min-height: ${minHeightMobile};
  `};
`

const MainHeaderContainer = styled.div`
  padding: 4px 0 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const LogoImg = styled.img`
  height: 35px;
  display: block;
`

const Row = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  max-width: 1000px;
  ${props => props.theme.media.mobile`
    min-height: 50px;
    width: 90%;
  `};
`

const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const MobileMenuRow = styled.div`
  opacity: ${props => (props.open ? "1" : "0")};
  transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
  max-height: ${props => (props.open ? "1000px" : "0px")};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  align-items: center;
`

const NavLink = styled.a`
  display: inline-flex;
  font-size: 0.75em;
  color: #8a8a8a;
  margin: 0 0.7em;
  ${props => props.theme.media.smallDesktop`
    margin: 0.6em 0;
  `};
  cursor: pointer;
  font-family: ${props => props.theme.fonts.avenirNextRegular};
  text-decoration: none;
  text-align: center;
  &:hover {
    text-decoration: underline;
  }
`

const RenderLinks = ({ links, isMobile, showSubmitTicket, isLoggedIn }) => {
  const translate = useTranslations('header')

  return (
    <>
      {links.map(item => {
        if (item.external) {
          return (
            <NavLink href={item.href} key={item.title}>
              {item.title.toUpperCase()}
            </NavLink>
          )
        }
        if (item.href) {
          return (
            <Link href={item.href} key={item.title} passHref>
              <NavLink>{item.title.toUpperCase()}</NavLink>
            </Link>
          )
        }
        if (item.onClick) {
          return (
            <NavLink onClick={item.onClick} key={item.title}>
              {item.title.toUpperCase()}
            </NavLink>
          )
        }
      })}
      {isLoggedIn ? (
        <>
          {showSubmitTicket ? <SubmitTicketButton isMobile={isMobile} /> : null}
          <UserAvatar />
        </>
      ) : (
        <>
          <LoginModal opener={({ onClick }) => (
            <WinItButton
              style={{ marginLeft: "0.7em" }}
              height={27}
              fontSize="0.75em"
              shape="square"
              onClick={onClick}
              padding="2px 15px"
            >
              {translate('login')}
            </WinItButton>
              )}
          />
          {!isMobile ? ( 
            <LoginModal opener={({ onClick }) => <NavLink onClick={onClick} />} />
          ) : null}
        </>
      )}
    </>
  )
}

const mapStore = store => ({
  isMobile: store.ui.isMobile,
  isSmallDesktop: store.ui.isSmallDesktop,
  currentHost: store.ui.currentHost,
  isLoggedIn: store.auth.isLoggedIn
})

const Header = ({ showSubmitTicket = true }) => {
  const { isMobile, isSmallDesktop, isLoggedIn, currentHost } = useInject(mapStore)
  const translate = useTranslations('header')
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  const toggleMobileMenu = useCallback(() => {
    setShowMobileMenu(!showMobileMenu)
  })

  const links = [
    {
      title: translate('links.knowledgeBase'),
      external: false,
      href: `/knowledge-base`
    },
    {
      title: translate('links.business'),
      external: false,
      href: `/business`
    },
    {
      title: translate('links.contact'),
      external: false,
      href: `/contact`
    },
    {
      title: translate('links.blog'),
      external: true,
      href: "https://blog.appwinit.com",
    }
  ]

  useEffect(() => {
    if (!isSmallDesktop && showMobileMenu) {
      toggleMobileMenu()
    }
  }, [isSmallDesktop, showMobileMenu])

  let logoIMG = winitLogo
  let headerLinks = (
    <RenderLinks
      links={links}
      isLoggedIn={isLoggedIn}
      showSubmitTicket={showSubmitTicket}
    />
)
  if ((currentHost && currentHost.includes('paynyctickets.com'))) {
    logoIMG = payNYCTicketsLogo
    headerLinks = null
  }

  const isTrafficViolations = currentHost?.includes("trafficviolation")
  const logoLink = isTrafficViolations
    ? <a href="https://www.appwinit.com/"><LogoImg src={logoIMG} alt='WinIt Logo' /></a>
    : <Link href="/"><a><LogoImg src={logoIMG} alt='WinIt Logo' /></a></Link>

  return (
    <HeaderContainer>
      <MainHeaderContainer isMobile={isMobile}>
        <DesktopContainer compact>
          <Row isMobile={isMobile}>
            <LogoContainer>
              {logoLink}
            </LogoContainer>
            <LinkContainer>
              {isSmallDesktop ? (
                <>
                  {!isLoggedIn ? (
                    <SignupModal
                      opener={({ onClick }) => (
                        <WinItButton
                          style={{ marginRight: "0.7em" }}
                          height={27}
                          fontSize="0.75em"
                          shape="round"
                          onClick={onClick}
                          padding="2px 15px"
                        >
                          {translate('signUp')}
                        </WinItButton>
                      )}
                    />
                  ) : null}
                  <Spacer direction="width" mobile={16} desktop={16} />
                  <Hamburger toggle={toggleMobileMenu} open={showMobileMenu} />
                </>
              ) : headerLinks}
            </LinkContainer>
          </Row>
        </DesktopContainer>
      </MainHeaderContainer>
      <DesktopContainer compact>
        <MobileMenuRow open={showMobileMenu}>
          <Spacer mobile={16} desktop={16} />
          <RenderLinks
            links={links}
            isLoggedIn={isLoggedIn}
            showSubmitTicket={showSubmitTicket}
            isMobile
          />
          <Spacer mobile={16} desktop={16} />
        </MobileMenuRow>
      </DesktopContainer>
    </HeaderContainer>
  )
}

export default observer(Header)
